import React, {
  useCallback,
  useState,
  FunctionComponent,
  useEffect,
  useRef,
} from "react";
import {
  PlaidLink,
  PlaidLinkOnEvent,
  PlaidLinkOnExit,
  PlaidLinkOnSuccess,
  usePlaidLink,
} from "react-plaid-link";
import Confirmation from "./Confirmation";
import { isBot, detectBot, logBotDetection } from "./utils/CrawlerDetection";

interface BotDetectionDetails {
  isBot: boolean;
  reason: string;
  userAgent: string;
  referrer: string;
  timestamp: string;
}

const PayByBank: FunctionComponent = (props: any) => {
  const [token, setToken] = useState<string | null>(null);
  const [isBotDetected, setIsBotDetected] = useState<boolean>(false);
  const [botDetectionDetails, setBotDetectionDetails] = useState<BotDetectionDetails | null>(null);

  // Check for bots on component mount
  useEffect(() => {
    const detection = detectBot() as BotDetectionDetails;
    setBotDetectionDetails(detection);
    
    if (detection.isBot) {
      setIsBotDetected(true);
      logBotDetection(detection);
      console.warn('Bot detected - preventing Plaid Link initialization');
      return;
    }
  }, []);

  // generate a link_token only if not a bot
  useEffect(() => {
    if (isBotDetected) {
      console.log('Skipping Plaid Link token creation for bot');
      return;
    }

    async function createLinkToken() {
      try {
        const url: string =
          process.env.REACT_APP_PLAID_CREATE_LINK_TOKEN_URL ?? "";
        let response = await fetch(url, {
          method: "POST",
        });
        const token = await response.json();
        console.log("token is", token);
        setToken(token);
      } catch (error) {
        console.error("Error creating Plaid Link token:", error);
      }
    }
    createLinkToken();
  }, [isBotDetected]);

  const onSuccess = useCallback<PlaidLinkOnSuccess>(
    (public_token, metadata) => {
      const accountID = metadata.accounts[0]["id"];
      const accountType = metadata.accounts[0]["subtype"];

      props.makePlaidPayment(
        "plaid",
        accountType,
        public_token,
        accountID,
        0,
        0
      );
    },
    []
  );

  const onEvent = useCallback<PlaidLinkOnEvent>((eventName, metadata) => {
    // log onEvent callbacks from Link
    // https://plaid.com/docs/link/web/#onevent
    // console.log(eventName, metadata);
    if (eventName == "OPEN") {
      props.setLoad(true);
    }
  }, []);

  const onExit = useCallback<PlaidLinkOnExit>((error, metadata) => {
    // log onExit callbacks from Link, handle errors
    // https://plaid.com/docs/link/web/#onexit
    // console.log(error, metadata);
    props.setLoad(false);
  }, []);

  const { open, ready, error, exit } = usePlaidLink({
    token,
    onSuccess,
    onEvent,
    onExit,
  });

  const openModal = () => {
    // Additional bot check before opening modal
    if (isBot()) {
      console.warn('Bot detected during modal open attempt - blocking access');
      return;
    }

    if (props.goToPayment("PLAID")) {
      open();
    }
  };

  // If bot is detected, show a fallback or nothing
  if (isBotDetected) {
    return (
      <div
        style={{
          color: "#666666",
          background: "#f5f5f5",
          width: "100%",
          borderRadius: "5px",
          border: "1px solid #ddd",
          height: "45px",
          fontSize: "22px",
          fontWeight: 560,
          paddingTop: "5px",
          cursor: "not-allowed",
        }}
        className="text-center"
        title="Bank connection not available for automated requests"
      >
        Instant Bank Connection
      </div>
    );
  }

  // The pre-built PlaidLink component uses the usePlaidLink hook under the hood.
  // It renders a styled button element and accepts a `className` and/or `style` prop
  // to override the default styles. It accepts any Link config option as a prop such
  // as receivedRedirectUri, onEvent, onExit, onLoad, etc.
  return token === null ? (
    // insert your loading animation here
    <div className="loader"></div>
  ) : (
    <div
      onClick={openModal}
      style={{
        color: "#FFFFFF",
        background: "#000000",
        width: "100%",
        cursor: "pointer",
        borderRadius: "5px",
        border: "none",
        height: "45px",
        fontSize: "22px",
        fontWeight: 560,
        paddingTop: "5px",
      }}
      className="text-center"
    >
      Instant Bank Connection
    </div>
  );
};

export default PayByBank;
